import {
  APP_ERROR_TYPE,
  ERROR_USER_INTERFACE,
  useAppErrorHandler,
} from '@issuemanager/apps/client/contexts';
import { IssuePermissions } from '@issuemanager/constants';
import React from 'react';
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import { useIssuePermissions } from '../../features';
import { useIssueOutletContext } from '../../pages/issue/hooks/useIssueOutletContext';
import { FileUploader, ListFile } from './file-uploader';

interface ControlledFileUploaderProps<TFieldValues extends FieldValues>
  extends UseControllerProps<TFieldValues> {
  fullWidth?: boolean;
  label?: string;
  initialFileList?: ListFile[];
  marginRight?: string;
  permissions: IssuePermissions[];
  disabled?: boolean;
  hideDeleteButton?: boolean;
}

export function ControlledFileUploader<TFieldValues extends FieldValues>({
  control,
  name,
  fullWidth = true,
  label = '',
  permissions,
  ...rest
}: ControlledFileUploaderProps<TFieldValues>): React.JSX.Element {
  const issueOutletContext = useIssueOutletContext();
  // In create-issue-form we don't have issue context so we can pass undefined
  const issueCode = issueOutletContext?.code;
  const issuePermissions = useIssuePermissions(issueCode);
  const appErrorHandler = useAppErrorHandler();

  const hasUploadValidationError = async (): Promise<boolean> => {
    if (permissions.length > 0 && issueCode) {
      const { data: issuePermissionsData } = await issuePermissions.refetch();
      if (!issuePermissionsData) return false;
      const canUpload = permissions.some(
        (role) => issuePermissionsData[role] === true,
      );
      if (!canUpload) {
        appErrorHandler.setError({
          type: APP_ERROR_TYPE.USER_CANNOT_UPDATE_ACTION,
          userInterface: ERROR_USER_INTERFACE.DIALOG,
        });
        return true;
      }
    }
    return false;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => (
        <FileUploader
          value={value ?? []}
          onChange={onChange}
          hasUploadValidationError={hasUploadValidationError}
          fullWidth={fullWidth}
          name={name}
          label={label}
          initialFileList={value ?? []}
          {...rest}
        />
      )}
    />
  );
}
